import { routeConstants } from 'app/routes/route-constants';
import { CodetableTranslation } from 'app/area/codetable/constants';
import { CodeEditSectionType, CodeActionType, MetroLineConfigurationPayload } from 'app/area/codetable/item/types';
import { Configuration } from 'app/components/metro-line/types';
import { CodeFieldFlagWireKey, CodeFieldWireKey, CodetableFeature } from 'app/area/codetable/types-store';
import { shouldRenderDetails, shouldRenderVisibility, shouldRenderTranslations,
  shouldRenderTypes } from 'app/area/codetable/item/codetable-item-form-render-helpers';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { validationHighlights } from 'app/area/administration/shared/helpers';
import { ScreenPicker, ScreenField } from 'app/area/codetable/item/screen-renderer/types';

export const metroLineConfiguration = (payload: MetroLineConfigurationPayload): Configuration<CodeEditSectionType>[] => {

  const { breadcrumbs, codeId, actionType } = payload;

  const toAddingCodeUrl = (configurationItem: Configuration<CodeEditSectionType>): Configuration<CodeEditSectionType> =>
    !isDefinedIncludingZero(codeId)
      ? {
        ...configurationItem,
        url: routeConstants.codetableAdministrationAddCodeSectionFor(breadcrumbs, configurationItem.section)
      }
      : configurationItem;

  const filterInvisibles = (configurationItem: Configuration<CodeEditSectionType>): boolean =>
    !!configurationItem && configurationItem.visible;

  const configuration = getScreenConfiguration(payload).filter(filterInvisibles).map(toAddingCodeUrl);

  const translationsIndex = configuration.findIndex(configurationItem => configurationItem.section === CodeEditSectionType.Translations);

  const shouldShowTranslationsFirst = {
    [CodeActionType.Add]: true,
    [CodeActionType.Edit]: false,
    [CodeActionType.Duplicate]: true
  }[actionType];

  const shouldShowTranslations = translationsIndex > -1;

  return shouldShowTranslations && shouldShowTranslationsFirst
    ? [configuration[translationsIndex],
    ...configuration.filter(configurationItem => configurationItem.section !== CodeEditSectionType.Translations)]
    : configuration;
};

const getScreenConfiguration = (payload: MetroLineConfigurationPayload) => {
  const { supportedScreens, metadata } = payload;

  if (!supportedScreens || supportedScreens.length === 0 || !metadata || !metadata.codetable
    || !isDefinedIncludingZero(metadata.codetable.id) || !metadata.formName) {
    return defaultConfiguration(payload);
  }

  const screenData = supportedScreens.find(screen => screen.id === metadata.codetable.id && screen.name === metadata.formName);
  return !!screenData && !!screenData.screenConfiguration ? screenData.screenConfiguration(payload) : defaultConfiguration(payload);
};

const defaultConfiguration = ({
  detailsSectionRef, visibilitySectionRef, translationsSectionRef, typesRef, metadata,
  breadcrumbs, codeId, actionType, validationErrors, supportedScreens
}: MetroLineConfigurationPayload): Configuration<CodeEditSectionType>[] => {

  const screenData = supportedScreens.find(screen => screen.id === metadata.codetable.id && screen.name === metadata.formName);

  return [
    {
      section: CodeEditSectionType.Details,
      visible: shouldRenderDetails(metadata, screenData?.screenSlots?.details),
      iconUrl: '/i/case-sections/default.svg',
      url: routeConstants.codetableAdministrationCodeSectionFor(breadcrumbs, codeId, CodeEditSectionType.Details, actionType),
      label: (translation: CodetableTranslation) => translation.details,
      ref: detailsSectionRef,
      isValid: validationHighlights([CodeFieldFlagWireKey.scoreCalcWeight,
      CodetableFeature.parent, CodetableFeature.node, CodetableFeature.category, CodetableFeature.icon, CodetableFeature.icon2, 
      CodeFieldWireKey.colourCode, ScreenPicker.likelihood, ScreenPicker.recurrence, ScreenPicker.severity, ScreenPicker.uom,
      ScreenField.low, ScreenField.mid, ScreenField.high, ScreenField.scoreCalcWeight, ScreenField.companyRegisterId,
      ScreenPicker.uomTypeFrom, ScreenPicker.uomTypeTo], validationErrors)
    },
    {
      section: CodeEditSectionType.Visibility,
      visible: shouldRenderVisibility(metadata, screenData?.screenSlots?.visibility),
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: routeConstants.codetableAdministrationCodeSectionFor(breadcrumbs, codeId, CodeEditSectionType.Visibility, actionType),
      label: (translation: CodetableTranslation) => translation.visibility,
      ref: visibilitySectionRef,
      isValid: validationHighlights([CodeFieldWireKey.application, CodeFieldWireKey.subset], validationErrors)
    },
    {
      section: CodeEditSectionType.Types,
      visible: shouldRenderTypes(metadata, screenData?.screenSlots?.types),
      iconUrl: '/i/case-sections/default.svg',
      url: routeConstants.codetableAdministrationCodeSectionFor(breadcrumbs, codeId, CodeEditSectionType.Types, actionType),
      label: (translation: CodetableTranslation) => translation.types,
      ref: typesRef,
      isValid: validationHighlights([CodeFieldWireKey.class], validationErrors)
    },
    {
      section: CodeEditSectionType.Translations,
      visible: shouldRenderTranslations(metadata, screenData?.screenSlots?.translations),
      url: routeConstants.codetableAdministrationCodeSectionFor(breadcrumbs, codeId, CodeEditSectionType.Translations, actionType),
      iconUrl: '/i/case-sections/actions.svg',
      label: (translation: CodetableTranslation) => translation.descriptions_and_translations,
      ref: translationsSectionRef,
      isValid: validationHighlights(['language'], validationErrors)
    }
  ];
};
